import { useMemo } from 'react';
import { useCollaboratorInvitations } from '../../../../hooks/api/useCollaboratorInvitations';
import { useMyOrganizationClientInvitations } from '../../../../hooks/api/useMyOrganizationClientInvitations';
import useMyOrganizationInvitations from '../../../../hooks/api/useMyOrganizationInvitations';
import { APITypes, OrganizationTypes } from '../../../../primary-record-types';
import getName from '../../../../utils/getName';
import { getIntl } from '../../../../utils/intl';
import { organizationRoles } from '../../../../utils/sharedMessages';
import { useExplicitAppType } from '../../../../hooks/useAppType';

const intl = getIntl('userRegistrationDataProvider', {
    registration: 'Registration',
    organizationClientInvitationHeader: 'Complete Registration as a Client With {organization}',
    organizationUserInvitationHeader: 'Complete Registration as a {role} With {organization}',
    collaboratorInvitationHeader: 'Complete Registration as a Collaborator For {profile}',
    aProfile: 'a Profile',
    anOrganization: 'an Organization',
});

type OrganizationClientInvitationIntentValues = {
    type: 'client-invitation';
    organizationName: string;
};

type CollaboratorInvitationIntentValues = {
    type: 'collaborator-invitation';
    profileName: string;
};

type OrganizationUserIntentValues = {
    type: 'organization-user';
    organizationName: string;
    role: OrganizationTypes.OrganizationUserRole;
};

export type UserRegistrationIntent =
    | OrganizationClientInvitationIntentValues
    | CollaboratorInvitationIntentValues
    | OrganizationUserIntentValues;

const getHeaderTitle = (intent: UserRegistrationIntent | null): string => {
    if (!intent) {
        return intl('registration');
    }

    switch (intent.type) {
        case 'client-invitation':
            return intl('organizationClientInvitationHeader', {
                organization: intent.organizationName || intl('anOrganization'),
            });
        case 'collaborator-invitation':
            return intl('collaboratorInvitationHeader', { profile: intent.profileName || intl('aProfile') });
        case 'organization-user':
            return intl('organizationUserInvitationHeader', {
                organization: intent.organizationName || intl('anOrganization'),
                role: organizationRoles(intent.role),
            });
    }
};

export type UserRegistrationData = {
    title: string;
    defaultValues: APITypes.userService.User;
};

export type UserRegistrationDataProviderProps = {
    initialUserValue: APITypes.userService.User;
    children: (data: UserRegistrationData | 'loading') => React.ReactNode;
};

const getDefaultValues = (
    initialUserValue: APITypes.userService.User,
    valuesFromData: Partial<APITypes.userService.User>,
): APITypes.userService.User => {
    return {
        ...initialUserValue,
        firstName: valuesFromData.firstName || initialUserValue.firstName,
        lastName: valuesFromData.lastName || initialUserValue.lastName,
        dob: valuesFromData.dob || initialUserValue.dob,
        preferredName: valuesFromData.preferredName || initialUserValue.preferredName,
    };
};

const ProfessionalUserDataProvider: React.FC<UserRegistrationDataProviderProps> = ({ initialUserValue, children }) => {
    const { isLoadingMyOrganizationInvitation, myOrganizationInvitation } = useMyOrganizationInvitations({});

    const defaultValues = useMemo<APITypes.userService.User>(() => {
        if (!myOrganizationInvitation) return initialUserValue;

        const valuesFromData: Partial<APITypes.userService.User> = {
            firstName: myOrganizationInvitation.inviteeFirstName,
            lastName: myOrganizationInvitation.inviteeLastName,
        };
        return getDefaultValues(initialUserValue, valuesFromData);
    }, [initialUserValue, myOrganizationInvitation]);

    if (isLoadingMyOrganizationInvitation) {
        return children('loading');
    }

    if (!myOrganizationInvitation) {
        return children({
            title: intl('registration'),
            defaultValues,
        });
    }

    return children({
        title: getHeaderTitle({
            type: 'organization-user',
            organizationName: myOrganizationInvitation.organization.metadata.name,
            role: myOrganizationInvitation.inviteeRole,
        }),
        defaultValues,
    });
};

const ConsumerUserDataProvider: React.FC<UserRegistrationDataProviderProps> = ({ initialUserValue, children }) => {
    const { isLoadingClientInvitations, clientInvitations } = useMyOrganizationClientInvitations({});
    const { isLoadingInvitations, invitations } = useCollaboratorInvitations({});
    const clientInvitation = clientInvitations?.[0];
    const collaboratorInvitation = invitations?.[0];

    const defaultValues = useMemo<APITypes.userService.User>(() => {
        if (clientInvitation) {
            return getDefaultValues(initialUserValue, {
                firstName: clientInvitation.clientOrganizerFirstName,
                lastName: clientInvitation.clientOrganizerLastName,
            });
        }

        if (collaboratorInvitation) {
            return getDefaultValues(initialUserValue, {
                firstName: collaboratorInvitation.inviteeFirstName,
                lastName: collaboratorInvitation.inviteeLastName,
            });
        }

        return initialUserValue;
    }, [clientInvitation, collaboratorInvitation, initialUserValue]);

    if (isLoadingClientInvitations || isLoadingInvitations) {
        return children('loading');
    }

    if (clientInvitation) {
        return children({
            title: getHeaderTitle({
                type: 'client-invitation',
                organizationName: clientInvitation.organization.metadata.name,
            }),
            defaultValues,
        });
    } else if (collaboratorInvitation) {
        return children({
            title: getHeaderTitle({
                type: 'collaborator-invitation',
                profileName: getName(collaboratorInvitation.medicalProfile),
            }),
            defaultValues,
        });
    } else {
        return children({
            title: intl('registration'),
            defaultValues,
        });
    }
};

export const UserRegistrationDataProvider: React.FC<UserRegistrationDataProviderProps> = (props) => {
    const appType = useExplicitAppType();
    return appType === 'professional' ? (
        <ProfessionalUserDataProvider {...props} />
    ) : (
        <ConsumerUserDataProvider {...props} />
    );
};
